export type ShareDimension = 2 | 3;
type Dimension = ShareDimension;
export type SharedMatrixValuesFor<D extends Dimension> = D extends 2
  ? [number, number, number, number]
  : [number, number, number, number, number, number, number, number, number];
export type SharedDraftMatrixFor<D extends Dimension> = D extends 2
  ? [string, string, string, string]
  : [string, string, string, string, string, string, string, string, string];
export type SharedDraftVectorFor<D extends Dimension> = D extends 2
  ? [string, string]
  : [string, string, string];

export interface SharedCameraSnapshot {
  readonly position: readonly [number, number, number];
  readonly target: readonly [number, number, number];
  readonly zoom: number;
}

export interface SharedMatrix<D extends Dimension> {
  label: string;
  entries: Readonly<SharedDraftMatrixFor<D>>;
  durationMs: number;
}

export interface SharedVector<D extends Dimension> {
  label: string;
  coordinates: Readonly<SharedDraftVectorFor<D>>;
  color: string;
}

export interface SharedEquation<D extends Dimension> {
  matrices: SharedMatrix<D>[];
  vectors: SharedVector<D>[];
  appliedTransform: SharedMatrixValuesFor<D>;
}

export type SharedPlayback =
  | { readonly status: "idle"; readonly mode: "steps" | "composed" }
  | {
      readonly status: "paused";
      readonly mode: "steps" | "composed";
      readonly elapsedMs: number;
    };

export interface ShareDocument {
  activeDimension: Dimension;
  showBasis: boolean;
  showGrid: boolean;
  playback: SharedPlayback;
  equations: {
    2: SharedEquation<2>;
    3: SharedEquation<3>;
  };
  cameras: {
    2: SharedCameraSnapshot;
    3: SharedCameraSnapshot;
  };
}

/**
 * Frozen wire-format limits. Changing editor policy must not silently change
 * which existing share documents can be decoded; revise the format explicitly.
 */
export const SHARE_MAX_EQUATION_NODES = 64;
export const SHARE_MAX_EXPRESSION_LENGTH = 100;
export const SHARE_MAX_ABSOLUTE_INPUT_VALUE = 100;
export const SHARE_MIN_MATRIX_DURATION_MS = 100;
export const SHARE_MAX_MATRIX_DURATION_MS = 3_000;
export const SHARE_MAX_MATRIX_LABEL_LENGTH = 2;
export const SHARE_MAX_TRANSFORM_VALUE = 1e30;
const SHARE_MATRIX_LABEL_PATTERN = /^[A-Z]{1,2}$/;
const SHARE_VECTOR_LABEL_PATTERN = /^v[1-9][0-9]{0,2}$/;
const SHARE_VECTOR_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * Defensive bounds for camera values received from untrusted share documents.
 * These intentionally exceed and remain independent of the viewport's current
 * interaction limits so UI tuning does not invalidate previously shared views.
 */
export const SHARE_MAX_SERIALIZED_CAMERA_COORDINATE = 1_000;
export const SHARE_MIN_SERIALIZED_CAMERA_ZOOM = 0.01;
export const SHARE_MAX_SERIALIZED_CAMERA_ZOOM = 100;

function sharedExpression(value: unknown): string {
  if (typeof value !== "string" || value.length > SHARE_MAX_EXPRESSION_LENGTH)
    throw new Error("Invalid expression draft");
  return value;
}

function sharedDraftMatrixValues<D extends Dimension>(
  dimension: D,
  values: readonly unknown[],
): SharedDraftMatrixFor<D> {
  const parsed = parseExpressions(values, dimension ** 2);
  return (
    dimension === 2
      ? [parsed[0], parsed[1], parsed[2], parsed[3]]
      : [
          parsed[0],
          parsed[1],
          parsed[2],
          parsed[3],
          parsed[4],
          parsed[5],
          parsed[6],
          parsed[7],
          parsed[8],
        ]
  ) as SharedDraftMatrixFor<D>;
}

function sharedDraftVectorValues<D extends Dimension>(
  dimension: D,
  values: readonly unknown[],
): SharedDraftVectorFor<D> {
  const parsed = parseExpressions(values, dimension);
  return (
    dimension === 2 ? [parsed[0], parsed[1]] : [parsed[0], parsed[1], parsed[2]]
  ) as SharedDraftVectorFor<D>;
}

function sharedMatrixLabel(value: unknown): string {
  return matchedString(value, SHARE_MATRIX_LABEL_PATTERN, "matrix label");
}

function sharedVectorLabel(value: unknown): string {
  return matchedString(value, SHARE_VECTOR_LABEL_PATTERN, "vector label");
}

function sharedVectorColor(value: unknown): string {
  return matchedString(
    value,
    SHARE_VECTOR_COLOR_PATTERN,
    "vector color",
  ).toLowerCase();
}

function sharedMatrixDuration(value: unknown): number {
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    value < SHARE_MIN_MATRIX_DURATION_MS ||
    value > SHARE_MAX_MATRIX_DURATION_MS
  )
    throw new Error("Invalid matrix duration");
  return value;
}

function parseExpressions(
  values: readonly unknown[],
  length: number,
): string[] {
  /* v8 ignore next -- the JSON schema validates tuple lengths before parsing */
  if (values.length !== length) throw new Error("Invalid draft values");
  return values.map(sharedExpression);
}

function matchedString(
  value: unknown,
  pattern: RegExp,
  description: string,
): string {
  if (typeof value !== "string" || !pattern.test(value))
    throw new Error(`Invalid ${description}`);
  return value;
}

const MAX_SHARE_ELAPSED_MS =
  SHARE_MAX_EQUATION_NODES * SHARE_MAX_MATRIX_DURATION_MS * 2;

export function parseShareDocument(value: unknown): ShareDocument {
  const share = record(value, "share document");
  const activeDimension = dimension(share.activeDimension);
  const equations = record(share.equations, "share equations");
  const cameras = record(share.cameras, "camera snapshots");
  return {
    activeDimension,
    showBasis: booleanValue(share.showBasis),
    showGrid: booleanValue(share.showGrid),
    playback: parsePlayback(share.playback),
    equations: {
      2: parseEquation(equations[2], 2),
      3: parseEquation(equations[3], 3),
    },
    cameras: {
      2: parseCamera(cameras[2]),
      3: parseCamera(cameras[3]),
    },
  };
}

function parsePlayback(value: unknown): SharedPlayback {
  const playback = record(value, "playback snapshot");
  const mode = playback.mode;
  if (mode !== "steps" && mode !== "composed")
    throw new Error("Invalid playback mode");
  if (playback.status === "idle") return { status: "idle", mode };
  if (playback.status !== "paused") throw new Error("Invalid playback status");
  return {
    status: "paused",
    mode,
    elapsedMs: finiteNumber(playback.elapsedMs, 0, MAX_SHARE_ELAPSED_MS),
  };
}

function parseEquation<D extends Dimension>(
  value: unknown,
  dimensionValue: D,
): SharedEquation<D> {
  const equation = record(value, "equation snapshot");
  const matrices = boundedArray(equation.matrices).map((value) => {
    const matrix = record(value, "matrix snapshot");
    return {
      label: sharedMatrixLabel(matrix.label),
      entries: sharedDraftMatrixValues(
        dimensionValue,
        tuple(matrix.entries, dimensionValue ** 2),
      ),
      durationMs: sharedMatrixDuration(matrix.durationMs),
    };
  });
  const vectors = boundedArray(equation.vectors).map((value) => {
    const vector = record(value, "vector snapshot");
    return {
      label: sharedVectorLabel(vector.label),
      coordinates: sharedDraftVectorValues(
        dimensionValue,
        tuple(vector.coordinates, dimensionValue),
      ),
      color: sharedVectorColor(vector.color),
    };
  });
  uniqueLabels(matrices);
  uniqueLabels(vectors);
  return {
    matrices,
    vectors,
    appliedTransform: matrixValues(
      dimensionValue,
      boundedNumbers(
        equation.appliedTransform,
        dimensionValue ** 2,
        SHARE_MAX_TRANSFORM_VALUE,
      ),
    ),
  };
}

function matrixValues<D extends Dimension>(
  dimensionValue: D,
  values: number[],
): SharedMatrixValuesFor<D> {
  return (
    dimensionValue === 2
      ? [values[0], values[1], values[2], values[3]]
      : [
          values[0],
          values[1],
          values[2],
          values[3],
          values[4],
          values[5],
          values[6],
          values[7],
          values[8],
        ]
  ) as SharedMatrixValuesFor<D>;
}

function vector3Values(values: number[]): [number, number, number] {
  return values as [number, number, number];
}

function parseCamera(value: unknown): SharedCameraSnapshot {
  const camera = record(value, "camera snapshot");
  return {
    position: vector3Values(
      boundedNumbers(
        camera.position,
        3,
        SHARE_MAX_SERIALIZED_CAMERA_COORDINATE,
      ),
    ),
    target: vector3Values(
      boundedNumbers(camera.target, 3, SHARE_MAX_SERIALIZED_CAMERA_COORDINATE),
    ),
    zoom: finiteNumber(
      camera.zoom,
      SHARE_MIN_SERIALIZED_CAMERA_ZOOM,
      SHARE_MAX_SERIALIZED_CAMERA_ZOOM,
    ),
  };
}

function boundedNumbers(
  value: unknown,
  length: number,
  limit: number,
): number[] {
  return tuple(value, length).map((item) => finiteNumber(item, -limit, limit));
}

function tuple(value: unknown, length: number): unknown[] {
  if (
    !Array.isArray(value) ||
    value.length !== length ||
    !hasEveryArrayIndex(value)
  )
    throw new Error("Invalid snapshot tuple");
  return value;
}

function boundedArray(value: unknown): unknown[] {
  if (
    !Array.isArray(value) ||
    value.length > SHARE_MAX_EQUATION_NODES ||
    !hasEveryArrayIndex(value)
  )
    throw new Error("Invalid snapshot collection");
  return value;
}

function hasEveryArrayIndex(value: unknown[]): boolean {
  for (let index = 0; index < value.length; index += 1) {
    if (!Object.hasOwn(value, index)) return false;
  }
  return true;
}

function uniqueLabels(items: readonly { readonly label: string }[]): void {
  if (new Set(items.map(({ label }) => label)).size !== items.length)
    throw new Error("Duplicate equation labels");
}

function dimension(value: unknown): Dimension {
  if (value !== 2 && value !== 3) throw new Error("Invalid dimension");
  return value;
}

function booleanValue(value: unknown): boolean {
  if (typeof value !== "boolean") throw new Error("Invalid boolean value");
  return value;
}

function finiteNumber(
  value: unknown,
  minimum: number,
  maximum: number,
): number {
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    value < minimum ||
    value > maximum
  )
    throw new Error("Invalid numeric value");
  return value;
}

function record(value: unknown, description: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value))
    throw new Error(`Invalid ${description}`);
  return value as Record<string, unknown>;
}
